<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { WObjectSubTypeSelect } from '@/widgets'
import { VIcons, VSelectSearch } from '@/shared/ui'
import { DateFieldList, LogicFieldList, NumberFieldList } from '@/entities/Inventory/TypeField'
import { ObjectSubTypesSelect } from '@/entities/Inventory/ObjectSubTypes'
import InventDateTypeField from '@/entities/Inventory/TypeField/ui/fields/InventDateTypeField.vue'
import type { FilterCondition } from '../model/type.ts'
import { useGlobalSearch } from '@/widgets/GlobalSearch'
import { filterSearchableFields, getSearchConditionsForFieldType, getSearchConditionsForPseudoField } from '../model/searchFieldSupport.ts'
import {
  emptyValueForCompareType,
  isRangeFilterValue,
  reshapeFilterValueForCompareType,
} from '../model/filterConditionUtils.ts'
import { enrichFieldsWithLegacyIds } from '../model/enrichFieldsWithLegacyIds.ts'
import {
  findFieldByIdOrLegacy,
  normalizeSearchFieldIds,
  resolveFieldId,
} from '../model/resolveFieldId.ts'
import {
  PSEUDO_SEARCH_FIELD_ID_PREFIX,
  PSEUDO_SEARCH_FIELD_TYPE,
  useLoadPseudoSearchFields,
} from '../model/pseudoSearchFields.ts'
import { useHttp, useTranslate } from '@/shared/lib'

const filter = defineModel<FilterCondition>('filter', { required: true })

defineProps({
  globalObjectTypeId: {
    type: Number,
    default: 0,
  },
})

const emits = defineEmits(['delete'])
const { sortList } = useGlobalSearch()
const http = useHttp()
const { trs } = useTranslate()

const fields = ref<any[]>([])
const field = ref<any>(null)
const compareTypes = ref<any[]>([])

/** Псевдо-размещения (filter_search) выбранного типа объекта. */
const pseudoObjectTypeId = computed(() => Number(filter.value.object_type_id || 0))
const { data: pseudoFields } = useLoadPseudoSearchFields(pseudoObjectTypeId)

/** Псевдо-поля с i18n-суффиксом «(вычисляемое)» для списка выбора поля. */
const pseudoPickerFields = computed(() =>
  (pseudoFields.value ?? []).map((item) => ({
    ...item,
    name: `${item.name}${trs('itf.search_pseudo_field_suffix')}`,
  })),
)

/** Обычные поля типа объекта + псевдо-размещения с включённым поиском, по алфавиту. */
const pickerFields = computed(() =>
  [...fields.value, ...pseudoPickerFields.value].sort((a, b) =>
    String(a.name ?? '').localeCompare(String(b.name ?? ''), 'ru', { sensitivity: 'base' }),
  ),
)

/** Выбранное поле — псевдо-размещение (фильтр по pseudo_sql через справочник). */
const isPseudoField = computed<boolean>(() => field.value?.type === PSEUDO_SEARCH_FIELD_TYPE)

/**
 * Флаг восстановления пресета: VSelect может эмитить update:modelValue при
 * подмене legacy→numeric field_id — нельзя сбрасывать compare_type/value.
 */
const isRestoringField = ref(false)

/**
 * Готовит список полей типа для пикера: отбрасывает неподдерживаемые поиском
 * типы и приводит id к строке (совместимость с пресетами / VSelect).
 */
const applyTypeFields = (rawFields: unknown[] | undefined) => {
  fields.value = normalizeSearchFieldIds(filterSearchableFields((rawFields as any[]) ?? []))
}

/**
 * Ссылка на справочник значений поля `list_types`.
 * Здесь лежит id строки catalog_invent_objects_type, а не object_type_id —
 * резолвом занимается ObjectSubTypesSelect.
 */
const listTypeId = computed<number>(() => Number(field.value?.object_type ?? 0))

/** Для псевдо-поля значения IN_LIST берутся из связанного справочника размещения. */
const inListTypeId = computed<number>(() =>
  isPseudoField.value ? Number(field.value?.dictionary_id ?? 0) : listTypeId.value,
)

/** Значения из описания поля — запасной источник, если справочник не отдал подтипы. */
const listFallbackItems = computed<unknown[]>(() =>
  Array.isArray(field.value?.items) ? field.value.items : [],
)

/**
 * При смене условия подгоняет форму `value`: RANGE → `{ from, to }`, иначе скаляр.
 * Без immediate — при загрузке пресета с уже корректным объектом значение не затирается.
 */
watch(
  () => filter.value.compare_type,
  (compareType) => {
    // Пустая строка в старых пресетах = «без условия».
    if (compareType === '') {
      filter.value.compare_type = null
      return
    }
    const next = reshapeFilterValueForCompareType(compareType, filter.value.value)
    if (next !== filter.value.value) {
      filter.value.value = next
    }
  },
)

/**
 * Безопасные биндинги границ RANGE: если `value` ещё строка/null (гонка или
 * старый пресет), сеттер сначала поднимает объект, а не пишет `.from` в `''`.
 */
const rangeFrom = computed({
  get: () => (isRangeFilterValue(filter.value.value) ? String(filter.value.value.from ?? '') : ''),
  set: (val: string) => {
    if (!isRangeFilterValue(filter.value.value)) {
      filter.value.value = { from: val, to: '' }
      return
    }
    filter.value.value.from = val
  },
})

const rangeTo = computed({
  get: () => (isRangeFilterValue(filter.value.value) ? String(filter.value.value.to ?? '') : ''),
  set: (val: string) => {
    if (!isRangeFilterValue(filter.value.value)) {
      filter.value.value = { from: '', to: val }
      return
    }
    filter.value.value.to = val
  },
})

/**
 * Обновляет выбранное поле и пересчитывает список доступных типов условий
 * на основе типа нового поля (см. `getSearchConditionsForFieldType`).
 * При resetDependents сбрасывает тип условия и значение в фильтре.
 *
 * Псевдо-размещение (`props:<id>`) всегда работает в режиме IN_LIST:
 * условие фиксировано, значение — массив id элементов связанного справочника.
 *
 * @param val             - id выбранного поля (null — поле не выбрано)
 * @param resetDependents - сбросить ли зависимые поля (compare_type, value)
 */
const setField = (val: string | null, resetDependents = false) => {
  field.value = findFieldByIdOrLegacy(pickerFields.value, val)

  if (field.value?.type === PSEUDO_SEARCH_FIELD_TYPE) {
    compareTypes.value = getSearchConditionsForPseudoField()
    filter.value.compare_type = 'IN_LIST'
    if (resetDependents) filter.value.value = []
    return
  }

  compareTypes.value = field.value ? getSearchConditionsForFieldType(field.value.type) : []

  if (resetDependents) {
    filter.value.compare_type = null
    filter.value.value = emptyValueForCompareType(null)
  }
}

/**
 * Восстанавливает field_id пресета: при необходимости дотягивает legacy
 * (fallback, если full ещё без legacy_field_id) и переписывает filter.field_id
 * на актуальный numeric id из справочника.
 */
const restoreFieldFromFilter = async () => {
  const typeId = Number(filter.value.object_type_id || 0)
  await enrichFieldsWithLegacyIds(typeId, fields.value, http)

  const resolved = resolveFieldId(fields.value, filter.value.field_id)
  isRestoringField.value = true
  try {
    if (resolved && resolved !== String(filter.value.field_id ?? '')) {
      filter.value.field_id = resolved
    }
    if (filter.value.compare_type === '') {
      filter.value.compare_type = null
    }
    setField(filter.value.field_id, false)
  } finally {
    await nextTick()
    isRestoringField.value = false
  }
}

/**
 * Вызывается, когда тип объекта был установлен программно (например, при загрузке).
 * Заполняет список полей (нормализованный каталог из full + фильтр searchable)
 * и восстанавливает выбранное поле из фильтра без сброса зависимостей.
 *
 * @param item - объект с массивом полей типа объекта
 */
const objectTypeResolved = async (item: { fields: any[] }) => {
  applyTypeFields(item.fields)
  await restoreFieldFromFilter()
}

/**
 * Вызывается при ручной смене типа объекта пользователем.
 * Обновляет список полей и сбрасывает текущий выбор поля и зависимые значения.
 *
 * @param item - объект с массивом полей нового типа объекта
 */
const objectTypeSelected = (item: { fields: any[] }) => {
  applyTypeFields(item.fields)
  filter.value.field_id = null
  setField(null, true)
}

/**
 * Псевдо-размещения подгружаются отдельным запросом: при восстановлении пресета
 * с `props:<id>` поле резолвится повторно, когда список псевдо-полей пришёл.
 */
watch(pseudoPickerFields, () => {
  const val = filter.value.field_id
  if (typeof val === 'string' && val.startsWith(PSEUDO_SEARCH_FIELD_ID_PREFIX)) {
    setField(val, false)
  }
})

/**
 * Обработчик ручной смены поля пользователем.
 * При программном restore (пресет / legacy→numeric) зависимые поля не трогаем.
 *
 * @param val - id нового выбранного поля
 */
const updateField = (val: string | null) => {
  if (isRestoringField.value) {
    setField(val, false)
    return
  }
  setField(val, true)
}

const isDateField = computed<boolean>(() => {
  if (!field.value) return false
  return (Object.values(DateFieldList) as string[]).includes(field.value.type)
})

/** Поле типа «Год» — показываем выбор года, а не полной даты (день/месяц ни на что не влияют). */
const isYearField = computed<boolean>(() => field.value?.type === DateFieldList.YEAR)

const isNumberField = computed<boolean>(() => {
  if (!field.value) return false
  return (Object.values(NumberFieldList) as string[]).includes(field.value.type)
})

const isBooleanField = computed<boolean>(() => {
  if (!field.value) return false
  return field.value.type === LogicFieldList.BOOLEAN
})

const boolItems = [
  { value: 'true', title: 'Да' },
  { value: 'false', title: 'Нет' },
]
</script>

<template>
  <div class="w-global-search__fields-row">
    <div class="w-global-search__drag-handle" title="Перетащить">
      <VIcons type="drag_indicator" />
    </div>

    <div
      class="w-global-search__fields-form"
      :class="{
        'w-global-search__fields-form--range': filter.compare_type === 'RANGE',
        'w-global-search__fields-form--range-date':
          filter.compare_type === 'RANGE' && (isDateField || isYearField),
      }"
    >
      <WObjectSubTypeSelect
        hide-details
        :label="$t('itf.filter')"
        :disabled="!!globalObjectTypeId"
        compact
        filtered
        v-model="filter.object_type_id"
        @selected="objectTypeSelected"
        @resolved="objectTypeResolved"
      />

      <VSelectSearch
        :items="pickerFields"
        :label="$t('itf.field')"
        :disabled="!pickerFields.length"
        hide-details
        v-model="filter.field_id"
        item-title="name"
        item-value="id"
        @update:model-value="updateField"
      />

      <VSelect
        :items="compareTypes"
        :label="$t('itf.filter_condition')"
        :disabled="!filter.field_id"
        hide-details
        v-model="filter.compare_type"
      />

      <template v-if="filter.compare_type === 'RANGE'">
        <div
          class="w-global-search__range-fields"
          :class="{ 'w-global-search__range-fields--date': isDateField || isYearField }"
        >
          <InventDateTypeField
            v-if="isYearField"
            type="year"
            :label="$t('itf.from')"
            hide-details
            v-model="rangeFrom"
          />
          <VTextField
            v-else
            :type="isDateField ? 'date' : isNumberField ? 'number' : 'text'"
            :label="$t('itf.from')"
            hide-details
            v-model="rangeFrom"
          />
          <InventDateTypeField
            v-if="isYearField"
            type="year"
            :label="$t('itf.to')"
            hide-details
            v-model="rangeTo"
          />
          <VTextField
            v-else
            :type="isDateField ? 'date' : isNumberField ? 'number' : 'text'"
            :label="$t('itf.to')"
            hide-details
            v-model="rangeTo"
          />
        </div>
      </template>

      <template v-else-if="filter.compare_type === 'IN_LIST'">
        <ObjectSubTypesSelect
          :type-id="inListTypeId"
          :fallback-items="listFallbackItems"
          :label="$t('itf.value')"
          :multiple="isPseudoField"
          hide-details
          v-model="filter.value"
        />
      </template>

      <template v-else-if="isBooleanField">
        <VSelect
          :items="boolItems"
          :disabled="!filter.compare_type"
          :label="$t('itf.value')"
          hide-details
          v-model="filter.value"
        />
      </template>

      <template v-else-if="isYearField">
        <InventDateTypeField
          type="year"
          :disabled="!filter.compare_type"
          :label="$t('itf.value')"
          hide-details
          v-model="filter.value"
        />
      </template>

      <template v-else>
        <VTextField
          :type="isDateField ? 'date' : isNumberField ? 'number' : 'text'"
          :disabled="!filter.compare_type"
          :label="$t('itf.value')"
          hide-details
          v-model="filter.value"
        />
      </template>

      <VSelect
        :disabled="!filter.compare_type"
        :label="$t('itf.sort')"
        :items="sortList"
        hide-details
        v-model="filter.sort"
      />
    </div>

    <div class="w-global-search__fields_center">
      <VTooltip location="top" :text="$t('itf.delete_preset_group')">
        <template #activator="{ props }">
          <VBtn
            color="error"
            variant="text"
            icon
            density="compact"
            @click="emits('delete')"
            v-bind="props"
          >
            <VIcons type="close" />
          </VBtn>
        </template>
      </VTooltip>
    </div>
  </div>
</template>
